/**
 * ShareableAuditCard — Instagram-square (1080×1080) PNG export of an
 * audit's top insights (T3 viralité).
 *
 * Rendered off-screen (position: fixed, opacity: 0) so html2canvas can
 * snapshot it. The user never sees this card in the regular UI — only the
 * generated PNG (download) + a share URL is shown.
 *
 * Branding : Network-iA gradient (blue → violet → pink), Cal Sans heading,
 * watermark bottom-right. Anonymisation : optionally hide the prospect's
 * full name (only first letter + last letter shown).
 */
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface ShareableAuditCardData {
  prospect_name: string;
  prospect_handle: string | null;
  platform: string | null;
  temperature: 'cold' | 'warm' | 'hot';
  score: number;
  category: string;
  headline: string;
  top_strengths: string[];
  top_pain_points: string[];
  anonymise?: boolean;
}

const TEMP_COLORS: Record<string, string> = {
  hot: '#ec4899',
  warm: '#f59e0b',
  cold: '#3b82f6',
};
const TEMP_LABEL_KEY: Record<string, string> = {
  hot: 'shareCard.temp.hot',
  warm: 'shareCard.temp.warm',
  cold: 'shareCard.temp.cold',
};

function anonymiseName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1] ?? '' : '';
  const initF = first.charAt(0).toUpperCase();
  const initL = last ? last.charAt(0).toUpperCase() : '';
  return initL ? `${initF}.${initL}.` : `${initF}.`;
}

export const ShareableAuditCard = forwardRef<HTMLDivElement, ShareableAuditCardData>(
  function ShareableAuditCard(data, ref) {
    const { t } = useTranslation();
    const temp = {
      color: TEMP_COLORS[data.temperature] ?? TEMP_COLORS.cold!,
      label: t(TEMP_LABEL_KEY[data.temperature] ?? TEMP_LABEL_KEY.cold!),
    };
    const name = data.anonymise ? anonymiseName(data.prospect_name) : data.prospect_name;

    return (
      <div
        ref={ref}
        style={{
          width: 1080,
          height: 1080,
          background:
            'linear-gradient(135deg, #f0f9ff 0%, #fdf4ff 50%, #fff1f2 100%)',
          padding: 72,
          fontFamily:
            '"Cal Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          color: '#0a1226',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background blurred gradient blobs */}
        <div
          style={{
            position: 'absolute',
            top: -200,
            right: -200,
            width: 600,
            height: 600,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(236,72,153,0.18), transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: -240,
            left: -160,
            width: 580,
            height: 580,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(59,130,246,0.18), transparent 70%)',
            filter: 'blur(40px)',
          }}
        />

        {/* Header */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 18px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(10,18,38,0.06)',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#a855f7',
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background:
                  'linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)',
              }}
            />
            {t('shareCard.badge')}
          </div>

          <h1
            style={{
              marginTop: 32,
              fontSize: 72,
              fontWeight: 800,
              lineHeight: 0.95,
              letterSpacing: '-0.03em',
            }}
          >
            {name}
          </h1>
          {data.prospect_handle && (
            <p
              style={{
                marginTop: 12,
                fontSize: 22,
                color: 'rgba(10,18,38,0.55)',
                fontFamily: '"Space Mono", ui-monospace, monospace',
              }}
            >
              {data.platform ? `${data.platform.toLowerCase()} · ` : ''}
              {data.prospect_handle}
            </p>
          )}

          <div
            style={{
              marginTop: 32,
              display: 'flex',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                padding: '10px 22px',
                borderRadius: 14,
                background: temp.color,
                color: '#fff',
                fontSize: 22,
                fontWeight: 800,
                letterSpacing: '0.08em',
              }}
            >
              {temp.label}
            </div>
            <div
              style={{
                padding: '10px 22px',
                borderRadius: 14,
                background: 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(10,18,38,0.08)',
                fontSize: 22,
                fontWeight: 800,
              }}
            >
              {data.score}/100
            </div>
            <div
              style={{
                padding: '10px 22px',
                borderRadius: 14,
                background: 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(10,18,38,0.08)',
                fontSize: 18,
                fontWeight: 700,
                color: 'rgba(10,18,38,0.65)',
              }}
            >
              {data.category}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p
            style={{
              fontSize: 30,
              lineHeight: 1.35,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: 'rgba(10,18,38,0.88)',
            }}
          >
            « {data.headline} »
          </p>
        </div>

        {/* Strengths + Pain points */}
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', gap: 24 }}>
          {data.top_strengths.length > 0 && (
            <div
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(10,18,38,0.08)',
                borderRadius: 22,
                padding: 28,
                backdropFilter: 'blur(10px)',
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#10b981',
                  marginBottom: 14,
                }}
              >
                {t('shareCard.strengths')}
              </p>
              {data.top_strengths.slice(0, 3).map((s, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 17,
                    lineHeight: 1.4,
                    marginBottom: 8,
                    color: 'rgba(10,18,38,0.85)',
                  }}
                >
                  ✓ {s}
                </p>
              ))}
            </div>
          )}
          {data.top_pain_points.length > 0 && (
            <div
              style={{
                flex: 1,
                background: 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(10,18,38,0.08)',
                borderRadius: 22,
                padding: 28,
                backdropFilter: 'blur(10px)',
              }}
            >
              <p
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#ec4899',
                  marginBottom: 14,
                }}
              >
                {t('shareCard.painPoints')}
              </p>
              {data.top_pain_points.slice(0, 3).map((s, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 17,
                    lineHeight: 1.4,
                    marginBottom: 8,
                    color: 'rgba(10,18,38,0.85)',
                  }}
                >
                  → {s}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Watermark */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <div>
            <p
              style={{
                fontSize: 13,
                fontFamily: '"Space Mono", ui-monospace, monospace',
                fontWeight: 700,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'rgba(10,18,38,0.45)',
              }}
            >
              Network-iA · Audit IA
            </p>
            <p
              style={{
                marginTop: 6,
                fontSize: 18,
                fontWeight: 700,
                color: 'rgba(10,18,38,0.75)',
              }}
            >
              network-ia.io
            </p>
          </div>
          <div
            style={{
              padding: '14px 26px',
              borderRadius: 16,
              background:
                'linear-gradient(135deg, #3b82f6, #a855f7, #ec4899)',
              color: '#fff',
              fontSize: 18,
              fontWeight: 800,
              letterSpacing: '0.05em',
              boxShadow: '0 8px 30px -8px rgba(168,85,247,0.5)',
            }}
          >
            {t('shareCard.footer')}
          </div>
        </div>
      </div>
    );
  },
);
